import React from 'react';
import { Badge } from "components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "components/ui/avatar";
import { Card, CardHeader, CardContent, CardFooter } from "components/ui/card";
import { Button } from "components/ui/button";
import { ButtonGroup } from "components/ui/button-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";
import { Task, TaskPriority, TaskLabels } from "shared/types/task";
import { 
  ArrowRightIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  BuildingApartmentIcon,
  UsersIcon,
  CalendarIcon,
  Star
} from "@phosphor-icons/react";
import { useUsers } from "shared/contexts/users.context";
import { calculateDaysDifference } from "shared/utils/get-dates";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";

interface TaskCardProps {
  task: Task;
  onClick: () => void;
  onUpdate: () => void;
  isHighlighted?: boolean;
}

export const TaskCard: React.FC<TaskCardProps> = ({ 
  task, 
  onClick, 
  onUpdate,
  isHighlighted = false 
}) => {
  const { users } = useUsers();
  const { session } = useSession();

  const getUserImageUrlById = (userId: string) => {
    const foundUser = users.find((user) => user.id === userId);
    return foundUser?.image_url || null;
  };

  const getUserName = (userId: string) => {
    const foundUser = users.find((user) => user.id === userId);
    return foundUser ? `${foundUser.first_name} ${foundUser.last_name}` : "Unknown";
  };

  const getPriorityStars = (priority: TaskPriority): number => {
    switch (priority) {
      case TaskPriority.URGENT:
        return 4;
      case TaskPriority.HIGH:
        return 3;
      case TaskPriority.MEDIUM:
        return 2;
      case TaskPriority.LOW:
        return 1;
      default:
        return 0;
    }
  };

  const getPriorityLabel = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.URGENT:
        return "Urgent";
      case TaskPriority.HIGH:
        return "Hoog";
      case TaskPriority.MEDIUM:
        return "Gemiddeld";
      case TaskPriority.LOW:
        return "Laag";
      default:
        return "Unknown";
    }
  };

  const completeTask = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const response = await fetchWithAuth(
        `${API_URL}/tasks/complete`,
        {
          headers: { "Content-Type": "application/json" },
          method: "PUT",
          body: JSON.stringify({ taskIds: [task.id] }),
        },
        session
      );

      if (!response.ok) {
        toast.error("Taak voltooien mislukt!");
        throw new Error("Failed to complete task.");
      } else {
        toast.success("Taak voltooid!");
        onUpdate();
      }
    } catch (error) {
      console.error("Error completing task:", error);
    }
  };

  const deleteTask = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/tasks`,
        {
          headers: { "Content-Type": "application/json" },
          method: "DELETE",
          body: JSON.stringify({ taskIds: [task.id] }),
        },
        session
      );

      if (!response.ok) {
        toast.error("Niet gelukt om taak te verwijderen");
        throw new Error("Failed to delete task.");
      } else {
        toast.success("Taak verwijderd!");
        onUpdate();
      }
    } catch (error) {
      console.error("Error deleting task:", error);
    }
  };

  const assigneeImage = getUserImageUrlById(task.assignee);
  const assignedByImage = getUserImageUrlById(task.assignedBy);
  const deadlineDisplay = task.deadline
    ? calculateDaysDifference("deadline", task.deadline)
    : null;
  const addedDisplay = calculateDaysDifference("added", task.inPhaseSince);
  const priorityStars = getPriorityStars(task.priority);

  return (
    <Card
      onClick={onClick}
      className={`group cursor-grab w-full max-w-full h-full border-none shadow-sm hover:shadow-xl hover:ring-2 hover:ring-offset-2 hover:ring-moss-primary transition-all duration-300 ${
        isHighlighted 
            ? 'ring-2 ring-offset-2 ring-moss-primary animate-pulse' 
            : ''
    }`}
    >
      <CardHeader className="space-y-3 p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Avatar className="h-7 w-7">
                    {assignedByImage ? (
                      <AvatarImage src={assignedByImage} alt={getUserName(task.assignedBy)} />
                    ) : null}
                    <AvatarFallback>
                      <UsersIcon size={14} />
                    </AvatarFallback>
                  </Avatar>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{getUserName(task.assignedBy)}</p>
              </TooltipContent>
            </Tooltip>
            <ArrowRightIcon size={14} className="text-muted-foreground" weight="bold" />
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Avatar className="h-7 w-7">
                    {assigneeImage ? (
                      <AvatarImage src={assigneeImage} alt={getUserName(task.assignee)} />
                    ) : null}
                    <AvatarFallback>
                      <UsersIcon size={14} />
                    </AvatarFallback>
                  </Avatar>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{getUserName(task.assignee)}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <ButtonGroup>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={completeTask}
                  size="icon"
                  variant="outline"
                >
                  <CheckCircleIcon size={18} weight="duotone" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Taak voltooien</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteTask();
                  }}
                >
                  <TrashIcon size={18} weight="duotone" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Taak verwijderen</p>
              </TooltipContent>
            </Tooltip>
          </ButtonGroup>
        </div>

        {/* Task ID */}
        <Badge variant="outline" className="w-fit">TA-{task.id}</Badge>

        {/* Title & Description */}
        <div className="space-y-1.5">
          <h4 className="font-semibold text-sm text-neutral-900 line-clamp-2 leading-tight">
            {task.title}
          </h4>
          {task.description && (
            <p className="text-xs text-neutral-500 line-clamp-2 leading-relaxed">
              {task.description}
            </p>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4 p-4 pt-0">
        {/* Priority & Labels */}
        <div className="flex flex-wrap gap-1.5">
          {priorityStars > 0 && (
            <div className="flex items-center gap-0.5">
              <span className="sr-only">{getPriorityLabel(task.priority)}</span>
              {[...Array(priorityStars)].map((_, index) => (
                <Star
                  key={index}
                  weight="fill"
                  size={12}
                  className="text-yellow-500"
                />
              ))}
            </div>
          )}
          {task.labels?.slice(0, 2).map((label: TaskLabels) => (
            <Badge key={label.id}>
              {label.name}
            </Badge>
          ))}
          {task.labels && task.labels.length > 2 && (
            <Badge>
              +{task.labels.length - 2}
            </Badge>
          )}
        </div>

        {/* Resources */}
        {task.resources && task.resources.length > 0 && (
          <div className="space-y-1.5">
            {task.resources.slice(0, 2).map((resource) => (
              <div
                key={resource.id}
                className="flex items-center gap-2.5 p-2 rounded-lg bg-neutral-50/50 border border-neutral-100 hover:border-moss-primary/20 hover:bg-moss-primary/5 transition-colors"
              >
                {resource.type === "building" ? (
                  <BuildingApartmentIcon size={16} className="text-moss-primary shrink-0" weight="duotone" />
                ) : (
                  <UsersIcon size={16} className="text-moss-primary shrink-0" weight="duotone" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate text-neutral-800">{resource.name}</p>
                  {resource.metadata1 && (
                    <p className="text-[11px] text-neutral-500 truncate">{resource.metadata1}</p>
                  )}
                </div>
              </div>
            ))}
            {task.resources.length > 2 && (
              <p className="text-xs text-neutral-500 font-medium">+{task.resources.length - 2} meer</p>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex-col items-start space-y-2 p-4 pt-0">
        {task.deadline && (
          <div className="flex items-center gap-2 text-neutral-600 bg-brick-primary/5 px-2.5 py-1.5 rounded-md">
            <CalendarIcon size={14} weight="duotone" className="shrink-0 text-brick-primary" />
            <p className="text-xs">
              <span className="text-neutral-500">Deadline:</span>{' '}
              <span className="font-semibold text-brick-primary">
                {deadlineDisplay ?? 'Onbekend'}
              </span>
            </p>
          </div>
        )}
        <div className="flex items-center gap-2 text-neutral-600">
          <ClockIcon size={14} weight="duotone" className="shrink-0 text-neutral-400" />
          <p className="text-xs">
            <span className="text-neutral-500">Toegevoegd:</span>{' '}
            <span className="font-medium text-neutral-700">
              {addedDisplay ?? 'Onbekend'}
            </span>
          </p>
        </div>
      </CardFooter>
    </Card>
  );
};
